import { useEffect, useRef, useState, useMemo, ChangeEvent } from 'react';
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormResetField,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { CitiesSharedServiceResponseI, CitySharedServiceI } from '@msc-b2c/interfaces/interfaces';
import {
  AppDispatch,
  CitiesSharedService,
  useDebounce,
  setAddressData,
  openLoader,
  closeLoader,
} from '@msc-b2c/shared-utils';

import { InputText } from '../../input/InputText';
import { OptionsBox } from '../../../optionsbox/OptionsBox';

import styles from './cityinput.module.scss';

type CityInputProps = {
  disabled?: boolean;
  label: string;
  name: string;
  isLowerLabel?: boolean;
  index: number;
  placeholder: string;
  cityValidationErrorMessage?: string;
  additionalValidationObj?: FieldValues;
  clearErrors: UseFormClearErrors<FieldValues>;
  errors: FieldErrors;
  setError: UseFormSetError<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  register: UseFormRegister<FieldValues>;
  resetField: UseFormResetField<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  validation?: RegisterOptions;
  selectedCountry: string;
  styleOptions?: string;
};

export function CityInput({
  disabled,
  isLowerLabel,
  index,
  label,
  name,
  placeholder,
  cityValidationErrorMessage = '',
  clearErrors,
  errors,
  setError,
  getValues,
  setValue,
  register,
  resetField,
  watch,
  validation,
  selectedCountry,
  styleOptions,
}: Readonly<CityInputProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const [addresses, setAddresses] = useState<CitySharedServiceI[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const townOptions = useMemo(
    () => addresses.map((address) => ({ label: address.town, value: address.town })),
    [addresses]
  );
  const selectedCity = watch(name);
  const ref = useRef<HTMLDivElement>(null);

  const handleInputChange = useDebounce((value: string) => {
    setValue(name, value, { shouldValidate: true });
  }, 600);

  const handleFocus = () => {
    setIsInputFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (ref.current && ref.current.contains(e.relatedTarget)) {
      return;
    }
    setIsInputFocused(false);
  };

  const handleOptionSelection = (value: string) => {
    setValue(name, value);
    setIsInputFocused(false);
    dispatch(setAddressData(addresses.filter((address) => address.town === value)));
  };

  useEffect(() => {
    if (!selectedCity?.trim() || selectedCity.length <= 1) {
      return;
    }

    openLoader('.loader');

    new CitiesSharedService(false, {
      countryCode: selectedCountry?.trim() ?? '',
      town: selectedCity.trim(),
    })
      .getData()
      .then((addressData: CitiesSharedServiceResponseI) => {
        if (!addressData?.towns?.length) {
          closeLoader('.loader');
          setError(name, {
            type: 'manual',
            message: cityValidationErrorMessage,
          });

          return;
        }

        setAddresses(addressData.towns.filter((address) => !!address?.town));
      })
      .catch((err) => {
        console.error('Error: ', err);
        setError(name, {
          type: 'manual',
          message: cityValidationErrorMessage,
        });
      })
      .finally(() => {
        closeLoader('.loader');
      });
  }, [selectedCity]);

  useEffect(() => {
    resetField(name, { keepDirty: false, keepError: false, keepTouched: false });
  }, [selectedCountry]);

  useEffect(() => {
    if (addresses.some((address) => address.town === selectedCity)) {
      handleOptionSelection(selectedCity);
    } else {
      dispatch(setAddressData([]));
    }
  }, [addresses]);

  return (
    <div
      className={styles['cmp-form-text__city-input-wrapper']}
      onBlur={handleBlur}
      ref={ref}
    >
      <InputText
        disabled={disabled}
        handleInputChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value)}
        index={index}
        isLowerLabel={isLowerLabel}
        label={label}
        name={name}
        placeholder={placeholder}
        clearErrors={clearErrors}
        errors={errors}
        register={register}
        validation={validation}
        styleOptions={`${styleOptions}`}
        onFocus={handleFocus}
      />
      <OptionsBox
        relatedFieldName={name}
        options={townOptions}
        isVisible={isInputFocused}
        getValues={getValues}
        onSelection={handleOptionSelection}
      />
    </div>
  );
}

export default CityInput;
